import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit2, FiPlus, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import TaskModal from '../../components/TaskModal';
import api from '../../services/api';

const ProjectTasksDetailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get('project');
  
  const [project, setProject] = useState(null);
  const [activeTasks, setActiveTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  
  const [showDescModal, setShowDescModal] = useState(false);
  const [descModalTask, setDescModalTask] = useState(null);
  const DESC_LIMIT = 150;
  const [activePagination, setActivePagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [completedPagination, setCompletedPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [activeLoading, setActiveLoading] = useState(false);
  const [completedLoading, setCompletedLoading] = useState(false);

  const fetchProjectAndTasks = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch project details
      const projectResponse = await api.post(`/projects/${projectId}`);
      setProject(projectResponse.data);
      fetchProjectMembers(projectId);
      
      // Fetch both active and completed tasks
      await Promise.all([
        fetchActiveTasks(1),
        fetchCompletedTasks(1)
      ]);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching project and tasks:', err);
      setError('Failed to load project tasks. Please try again.');
      setLoading(false);
    }
  }, [projectId]);

  const fetchActiveTasks = async (page = 1) => {
    try {
      setActiveLoading(true);
      
      console.log('Fetching active tasks for project:', projectId, 'page:', page);
      
      // For employees, we should only fetch tasks assigned to them
      // Use the employee tasks endpoint with project filter
      try {
        const response = await api.post('/employee/tasks/list', {
          project_id: projectId, page, limit: 10, status: 'active'
        });
        console.log('Employee active tasks API response:', response.data);
        
        if (response.data.tasks) {
          // Paginated response
          console.log('Using paginated response for active employee tasks');
          console.log('Tasks array:', response.data.tasks);
          console.log('Pagination object:', response.data.pagination);
          setActiveTasks(response.data.tasks);
          setActivePagination({
            page: response.data.pagination.page,
            totalPages: response.data.pagination.totalPages,
            total: response.data.pagination.total
          });
        } else {
          // Legacy response - filter client-side
          console.log('Using legacy response for active employee tasks - filtering client-side');
          console.log('All tasks received:', response.data);
          const filtered = response.data.filter(task => 
            task.status !== 'completed' && 
            task.project_id === parseInt(projectId)
          );
          console.log('Filtered active tasks for current user:', filtered.length, 'tasks');
          setActiveTasks(filtered);
          setActivePagination({ page: 1, totalPages: 1, total: filtered.length });
        }
      } catch (err) {
        console.error('Error fetching employee active tasks:', err);
        // If employee tasks fail, show empty state
        setActiveTasks([]);
        setActivePagination({ page: 1, totalPages: 1, total: 0 });
      }
    } catch (err) {
      console.error('Error fetching active tasks:', err);
    } finally {
      setActiveLoading(false);
    }
  };

  const fetchCompletedTasks = async (page = 1) => {
    try {
      setCompletedLoading(true);
      
      console.log('Fetching completed tasks for project:', projectId, 'page:', page);
      
      // For employees, we should only fetch tasks assigned to them
      // Use the employee tasks endpoint with project filter
      try {
        const response = await api.post('/employee/tasks/list', {
          project_id: projectId, page, limit: 10, status: 'completed'
        });
        console.log('Employee completed tasks API response:', response.data);
        
        if (response.data.tasks) {
          // Paginated response
          console.log('Using paginated response for completed employee tasks');
          console.log('Tasks array:', response.data.tasks);
          console.log('Pagination object:', response.data.pagination);
          setCompletedTasks(response.data.tasks);
          setCompletedPagination({
            page: response.data.pagination.page,
            totalPages: response.data.pagination.totalPages,
            total: response.data.pagination.total
          });
        } else {
          // Legacy response - filter client-side
          console.log('Using legacy response for completed employee tasks - filtering client-side');
          console.log('All tasks received:', response.data);
          const filtered = response.data.filter(task => 
            task.status === 'completed' && 
            task.project_id === parseInt(projectId)
          );
          console.log('Filtered completed tasks for current user:', filtered.length, 'tasks');
          setCompletedTasks(filtered);
          setCompletedPagination({ page: 1, totalPages: 1, total: filtered.length });
        }
      } catch (err) {
        console.error('Error fetching employee completed tasks:', err);
        // If employee tasks fail, show empty state
        setCompletedTasks([]);
        setCompletedPagination({ page: 1, totalPages: 1, total: 0 });
      }
    } catch (err) {
      console.error('Error fetching completed tasks:', err);
    } finally {
      setCompletedLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchProjectAndTasks();
      fetchClients();
      fetchUsers();
    } else {
      navigate('/employee/projects');
    }
  }, [projectId, navigate, fetchProjectAndTasks]);

  const fetchClients = async () => {
    try {
      const clientsResponse = await api.post('/clients/list');
      setClients(clientsResponse.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users', { params: { limit: 1000 } });
      setUsers(response.data.users || response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchProjectMembers = async (id) => {
    try {
      const response = await api.get(`/projects/${id}/members`);
      setProjectMembers(response.data.members || response.data);
    } catch (err) {
      console.error('Error fetching project members:', err);
    }
  };

  const handleCreateTask = () => {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentTask({
      project_id: project?.id,
      client_id: project?.client_id || project?.Client?.id,
      Users: [currentUser],
      assigned_users: [currentUser.id]
    });
    setShowTaskModal(true);
  };

  const handleEditTask = (task) => {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const users = task.Users || [];
    const alreadyIncluded = users.some(u => u.id === currentUser.id);
    setCurrentTask({
      ...task,
      Users: alreadyIncluded ? users : [currentUser, ...users],
      assigned_users: alreadyIncluded
        ? users.map(u => u.id)
        : [currentUser.id, ...users.map(u => u.id)]
    });
    setShowTaskModal(true);
  };

  const handleSaveTask = async (taskData) => {
    try {
      console.log('=== Task Save Debug ===');
      console.log('Current user:', JSON.parse(localStorage.getItem('user') || '{}'));
      console.log('Task data being sent:', taskData);
      console.log('Current task (for edit check):', currentTask);
      
      if (currentTask && currentTask.id) {
        // Update existing task
        console.log('Updating existing task:', currentTask.id);
        console.log('Assigned users being sent:', taskData.assigned_users);
        
        const response = await api.put(`/employee/tasks/${currentTask.id}`, taskData);
        console.log('Update response:', response.data);
        console.log('Updated task Users:', response.data.Users);
      } else {
        // Create new task - use the same approach as working TasksPage
        console.log('Creating new task via /tasks endpoint');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        
        const finalTaskData = {
          ...taskData,
          project_id: taskData.project_id ? parseInt(taskData.project_id) : null,
          client_id: taskData.client_id ? parseInt(taskData.client_id) : null,
        };
        
        console.log('Final task data for creation:', finalTaskData);
        const response = await api.post('/tasks', finalTaskData);
        console.log('Task creation response:', response.data);
      }
      
      setShowTaskModal(false);
      
      console.log('Refreshing tasks...');
      await Promise.all([
        fetchActiveTasks(activePagination.page),
        fetchCompletedTasks(completedPagination.page)
      ]);
      
      console.log('=== Task Save Complete ===');
    } catch (err) {
      console.error('=== Task Save Error ===');
      console.error('Error:', err.response?.data || err.message);
      console.error('Full error response:', err.response);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      alert(`Failed to save task: ${errorMessage}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    return date.toLocaleDateString() + ' ' + timeString.toUpperCase();
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      todo: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      testing: 'bg-purple-100 text-purple-800',
      client_review: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800'
    };
    
    const statusLabels = {
      todo: 'To Do',
      in_progress: 'In Progress',
      testing: 'Testing',
      client_review: 'Client Review',
      completed: 'Completed'
    };
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const getPriorityBadge = (priority, isCompleted = false) => {
    if (isCompleted) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-500">
          {priority.charAt(0).toUpperCase() + priority.slice(1)}
        </span>
      );
    }
    const priorityClasses = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-500 text-white animate-pulse'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${priorityClasses[priority] || 'bg-gray-100 text-gray-800'}`}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </span>
    );
  };

  // Filter tasks by status
  const renderPagination = (pagination, onPageChange, loading) => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= pagination.totalPages; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center text-sm text-gray-700">
          Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page <= 1 || loading}
            className="px-2 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            <FiChevronLeft />
          </button>
          {pages.map(page => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              disabled={loading}
              className={`px-3 py-1 border rounded ${
                page === pagination.page 
                  ? 'bg-blue-500 text-white border-blue-500' 
                  : 'hover:bg-gray-100'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages || loading}
            className="px-2 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            <FiChevronRight />
          </button>
        </div>
      </div>
    );
  };

  const renderTasksTable = (taskList, showEmpty = true, loading = false, isCompleted = false) => {
    if (loading) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p>Loading tasks...</p>
        </div>
      );
    }

    if (taskList.length === 0 && showEmpty) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p className="mb-4">No tasks found.</p>
        </div>
      );
    }

    if (taskList.length === 0) {
      return null;
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Task
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assigned To
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Due Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {taskList.map(task => (
              <tr key={task.id} className={!isCompleted && task.priority === 'high' ? 'bg-red-50' : 'hover:bg-gray-50'}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Link to={`/employee/tasks/${task.id}`} className="hover:text-blue-600">
                    <div className="text-sm font-medium text-gray-900">{task.title}</div>
                    {task.description && (
                      <button
                        type="button"
                        onClick={e => { e.preventDefault(); setDescModalTask(task); }}
                        className="text-xs text-blue-500 hover:text-blue-700 mt-0.5"
                      >
                        View description
                      </button>
                    )}
                    {task.Client && (
                      <div className="text-xs text-gray-500 mt-1">
                        Client: {task.Client.name}
                      </div>
                    )}
                  </Link>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(task.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getPriorityBadge(task.priority, isCompleted)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-1">
                    {task.Users && task.Users.length > 0 ? (
                      task.Users.slice(0, 3).map((user) => (
                        <div
                          key={user.id}
                          className="w-8 h-8 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-medium"
                          title={user.name}
                        >
                          {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                      ))
                    ) : (
                      <span className="text-xs text-gray-400">Unassigned</span>
                    )}
                    {task.Users && task.Users.length > 3 && (
                      <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-xs">
                        +{task.Users.length - 3}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatDate(task.due_date)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatDateTime(task.created_at)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleEditTask(task)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <FiEdit2 size={18} title="Edit Task" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-red-500">{error}</div>
        <button 
          onClick={fetchProjectAndTasks} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Link 
            to="/employee/projects"
            className="flex items-center text-gray-600 hover:text-gray-800 mr-4"
          >
            <FiArrowLeft className="mr-2" />
            Back to Projects
          </Link>
          <h1 className="text-2xl font-semibold">
            {project ? `Tasks for ${project.name}` : 'Project Tasks'}
          </h1>
        </div>
        <button
          onClick={handleCreateTask}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
        >
          <FiPlus className="mr-2" /> Create Task
        </button>
      </div>

      {/* Active Tasks Section */}
      <div className="flex gap-6">
        {/* Sidebar - Project Info */}
        {project && (
          <div className="w-64 flex-shrink-0">
            <div className="bg-white shadow-md rounded-lg p-4 sticky top-4">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Project Info</h2>
              <div className="mb-3">
                <p className="text-xs text-gray-500 mb-1">Name</p>
                <p className="text-sm font-medium text-gray-900">{project.name}</p>
              </div>
              {project.description && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Description</p>
                  <button
                    onClick={() => setShowDescModal(true)}
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    View Details
                  </button>
                </div>
              )}
              {project.Client && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Client</p>
                  <p className="text-sm text-gray-700">{project.Client.name}</p>
                </div>
              )}
              <div className="mb-3">
                <p className="text-xs text-gray-500 mb-1">Status</p>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  project.status === 'active' ? 'bg-green-100 text-green-800' :
                  project.status === 'on_hold' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-blue-100 text-blue-800'
                }`}>{project.status}</span>
              </div>
              {project.start_date && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Start Date</p>
                  <p className="text-sm text-gray-700">{new Date(project.start_date).toLocaleDateString()}</p>
                </div>
              )}
              {project.end_date && (
                <div className="mb-2">
                  <p className="text-xs text-gray-500 mb-1">End Date</p>
                  <p className="text-sm text-gray-700">{new Date(project.end_date).toLocaleDateString()}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 min-w-0">
        <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Active Tasks ({activePagination.total})
          </h2>
        </div>
        {activeTasks.length === 0 && !activeLoading ? (
          <div className="text-center py-8 text-gray-500">
            <p className="mb-4">No active tasks found for this project.</p>
            <button
              onClick={handleCreateTask}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center mx-auto"
            >
              <FiPlus className="mr-2" /> Create First Task
            </button>
          </div>
        ) : (
          <>
            {renderTasksTable(activeTasks, false, activeLoading, false)}
            {renderPagination(activePagination, (page) => fetchActiveTasks(page), activeLoading)}
          </>
        )}
      </div>

      {/* Completed Tasks Section */}
      {(completedTasks.length > 0 || completedPagination.total > 0) && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Completed Tasks ({completedPagination.total})
            </h2>
          </div>
          {renderTasksTable(completedTasks, false, completedLoading, true)}
          {renderPagination(completedPagination, (page) => fetchCompletedTasks(page), completedLoading)}
        </div>
      )}
        </div> {/* end main content */}
      </div> {/* end flex wrapper */}

      {showDescModal && project?.description && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center px-8 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{project.name} — Description</h3>
              <button onClick={() => setShowDescModal(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
            </div>
            <div className="overflow-y-auto px-8 py-6">
              <div className="prose prose-sm max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: project.description }} />
            </div>
          </div>
        </div>
      )}

      {descModalTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{descModalTask.title}</h3>
              <button onClick={() => setDescModalTask(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
            </div>
            <div className="overflow-y-auto px-6 py-5">
              <div className="prose prose-sm max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: descModalTask.description }} />
            </div>
          </div>
        </div>
      )}

      {showTaskModal && (
        <TaskModal
          task={currentTask}
          onClose={() => setShowTaskModal(false)}
          onSave={handleSaveTask}
          users={users}
          clients={clients}
          projects={project ? [project] : []}
          selectedProject={project}
          projectTeam={null}
        />
      )}
    </div>
  );
};

export default ProjectTasksDetailPage;